import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy, onSnapshot, deleteDoc, doc, updateDoc, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import appVersion from '../version';

function AdminDashboard() {
  const [responses, setResponses] = useState([]);
  const [carpools, setCarpools] = useState([]);
  const [userProfiles, setUserProfiles] = useState({}); // Map of userId -> profile data
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // 'all', 'yes', 'maybe', 'no', 'waitlist', 'confirmed', 'dropped'
  const [activeTab, setActiveTab] = useState('responses'); // 'responses', 'waitlist', 'carpools'
  const [assigningToCarpool, setAssigningToCarpool] = useState(null); // userId being assigned
  const { currentUser, signOut } = useAuth();
  const navigate = useNavigate();

  // Admin phone numbers (you can add more)
  const ADMIN_PHONE_NUMBERS = [
    '+15407939110',
  ];

  // Check if current user is an admin
  const isAdmin = currentUser && ADMIN_PHONE_NUMBERS.includes(currentUser.phoneNumber);

  const handleAdminLogout = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Failed to sign out:', error);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    
    const loadResponses = async () => {
      try {
        // Try to load from Firestore first
        const q = query(collection(db, 'responses'), orderBy('submittedAt', 'desc'));
        
        const unsubscribe = onSnapshot(q, 
          (querySnapshot) => {
            const data = querySnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            console.log('✅ Loaded from Firestore:', data.length, 'responses');
            setResponses(data);
            setLoading(false);
          },
          (error) => {
            console.warn('Firestore not available, loading from localStorage:', error);
            
            // Fallback: load from localStorage
            const localData = JSON.parse(localStorage.getItem('adminData') || '[]');
            console.log('📱 Loaded from localStorage:', localData.length, 'responses');
            // Convert string dates back to Date objects
            const processedData = localData.map(item => ({
              ...item,
              submittedAt: item.submittedAt ? new Date(item.submittedAt) : new Date()
            }));
            setResponses(processedData);
            setLoading(false);
          }
        );

        return () => unsubscribe();
      } catch (error) {
        console.warn('Error setting up Firestore listener, using localStorage:', error);
        
        // Fallback: load from localStorage
        const localData = JSON.parse(localStorage.getItem('adminData') || '[]');
        // Convert string dates back to Date objects
        const processedData = localData.map(item => ({
          ...item,
          submittedAt: item.submittedAt ? new Date(item.submittedAt) : new Date()
        }));
        setResponses(processedData);
        setLoading(false);
      }
    };

    const loadCarpools = () => {
      try {
        const carpoolsQuery = query(collection(db, 'carpools'), orderBy('createdAt', 'desc'));
        const carpoolsUnsubscribe = onSnapshot(
          carpoolsQuery,
          (snapshot) => {
            const carpoolsData = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            setCarpools(carpoolsData);
          },
          (err) => {
            console.warn('Error loading carpools:', err);
          }
        );

        return () => carpoolsUnsubscribe();
      } catch (error) {
        console.warn('Error setting up carpools listener:', error);
      }
    };

    const loadUserProfiles = async () => {
      try {
        if (!db) return;
        
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const profilesMap = {};
        usersSnapshot.forEach((doc) => {
          profilesMap[doc.id] = doc.data();
        });
        setUserProfiles(profilesMap);
      } catch (error) {
        console.warn('Error loading user profiles:', error);
      }
    };

    loadResponses();
    const carpoolsCleanup = loadCarpools();
    loadUserProfiles();
    
    return () => {
      if (carpoolsCleanup) carpoolsCleanup();
    };
  }, [isAdmin]);

  const getAvailabilityStatus = (response) => {
    // Use the mlkWeekendAvailability field directly
    const mlkStatus = response.answers?.mlkWeekendAvailability;
    
    if (!mlkStatus) return 'unavailable';
    return mlkStatus; // 'yes', 'maybe', or 'no'
  };

  const getFilteredResponses = () => {
    if (filter === 'all') return responses;
    if (filter === 'waitlist') return responses.filter(r => r.status === 'waitlist' || r.isWaitlist === true);
    if (filter === 'confirmed') return responses.filter(r => r.status === 'confirmed');
    if (filter === 'dropped') return responses.filter(r => r.status === 'dropped');
    
    return responses.filter(response => {
      const status = getAvailabilityStatus(response);
      return status === filter;
    });
  };

  const handleMoveToConfirmed = async (response) => {
    try {
      if (response.id && !response.id.startsWith('local-')) {
        await updateDoc(doc(db, 'responses', response.id), {
          status: 'confirmed',
          isWaitlist: false,
          updatedAt: new Date().toISOString()
        });
      } else {
        setError('Cannot update local-only responses. Please use Firestore.');
      }
    } catch (error) {
      console.error('Error moving to confirmed:', error);
      setError('Failed to update status. Please try again.');
    }
  };

  const handleMoveToWaitlist = async (response) => {
    try {
      if (response.id && !response.id.startsWith('local-')) {
        await updateDoc(doc(db, 'responses', response.id), {
          status: 'waitlist',
          isWaitlist: true,
          updatedAt: new Date().toISOString()
        });
      } else {
        setError('Cannot update local-only responses. Please use Firestore.');
      }
    } catch (error) {
      console.error('Error moving to waitlist:', error);
      setError('Failed to update status. Please try again.');
    }
  };

  const handleMarkAsDropped = async (response) => {
    if (!window.confirm(`Mark ${response.userName} as dropped out? They will be removed from confirmed participants.`)) {
      return;
    }

    try {
      if (response.id && !response.id.startsWith('local-')) {
        await updateDoc(doc(db, 'responses', response.id), {
          status: 'dropped',
          isWaitlist: false,
          updatedAt: new Date().toISOString()
        });
        
        // Remove from all carpools
        for (const carpool of carpools) {
          const passengers = carpool.passengers || [];
          if (passengers.includes(response.userId)) {
            const updatedPassengers = passengers.filter(uid => uid !== response.userId);
            await updateDoc(doc(db, 'carpools', carpool.id), {
              passengers: updatedPassengers,
              updatedAt: new Date().toISOString()
            });
          }
        }
      } else {
        setError('Cannot update local-only responses. Please use Firestore.');
      }
    } catch (error) {
      console.error('Error marking as dropped:', error);
      setError('Failed to update status. Please try again.');
    }
  };

  const handleAssignToCarpool = async (userId, carpoolId) => {
    try {
      const carpool = carpools.find(c => c.id === carpoolId);
      if (!carpool) return;

      const currentPassengers = carpool.passengers || [];
      
      // Check if already in carpool
      if (currentPassengers.includes(userId)) {
        setError('User is already in this carpool');
        setAssigningToCarpool(null);
        return;
      }

      // Check if carpool is full
      const availableSpots = carpool.maxSpots - currentPassengers.length;
      if (availableSpots <= 0) {
        setError('This carpool is full');
        setAssigningToCarpool(null);
        return;
      }

      // Remove from other carpools first
      const otherCarpools = carpools.filter(c => 
        c.id !== carpoolId && (c.passengers || []).includes(userId)
      );
      
      for (const otherCarpool of otherCarpools) {
        const updatedPassengers = (otherCarpool.passengers || []).filter(uid => uid !== userId);
        await updateDoc(doc(db, 'carpools', otherCarpool.id), {
          passengers: updatedPassengers,
          updatedAt: new Date().toISOString()
        });
      }

      // Add to selected carpool
      await updateDoc(doc(db, 'carpools', carpoolId), {
        passengers: [...currentPassengers, userId],
        updatedAt: new Date().toISOString()
      });

      setAssigningToCarpool(null);
      setError('');
    } catch (error) {
      console.error('Error assigning to carpool:', error);
      setError('Failed to assign to carpool. Please try again.');
      setAssigningToCarpool(null);
    }
  };

  const handleRemoveFromCarpool = async (userId) => {
    try {
      // Find which carpool the user is in
      const carpool = carpools.find(c => (c.passengers || []).includes(userId));
      if (!carpool) {
        setError('User is not in any carpool');
        return;
      }

      const updatedPassengers = (carpool.passengers || []).filter(uid => uid !== userId);
      await updateDoc(doc(db, 'carpools', carpool.id), {
        passengers: updatedPassengers,
        updatedAt: new Date().toISOString()
      });

      setError('');
    } catch (error) {
      console.error('Error removing from carpool:', error);
      setError('Failed to remove from carpool. Please try again.');
    }
  };

  const getUserCarpool = (userId) => {
    return carpools.find(c => (c.passengers || []).includes(userId));
  };

  const getAggregatedData = () => {
    const available = responses.filter(r => getAvailabilityStatus(r) === 'yes').length;
    const maybe = responses.filter(r => getAvailabilityStatus(r) === 'maybe').length;
    const unavailable = responses.filter(r => getAvailabilityStatus(r) === 'no').length;
    
    const sportPreferences = responses.reduce((acc, response) => {
      const sport = response.answers?.sport;
      if (sport) {
        acc[sport] = (acc[sport] || 0) + 1;
      }
      return acc;
    }, {});

    // Budget analysis
    const bucketSize = 50;
    const numericBudgets = [];
    let ballerCount = 0;

    responses.forEach(response => {
      const budgetValue = response.answers?.budget;
      const parsedBudget = typeof budgetValue === 'number' ? budgetValue : Number(budgetValue);
      const isBaller = !!response.answers?.budget_maxCheckbox;

      if (isBaller) {
        ballerCount += 1;
      }

      if (!Number.isNaN(parsedBudget)) {
        numericBudgets.push(parsedBudget);
      }
    });

    const averageBudget = numericBudgets.length > 0
      ? numericBudgets.reduce((sum, budget) => sum + budget, 0) / numericBudgets.length
      : 0;

    const minBudget = numericBudgets.length > 0 ? Math.min(...numericBudgets) : 0;
    const startBudget = Math.max(0, Math.floor(minBudget / bucketSize) * bucketSize);

    const formatCurrency = (value) => {
      return `$${value.toFixed(2).replace(/\.00$/, '')}`;
    };

    const bucketDefinitions = [];
    for (let bucketStart = startBudget; bucketStart < 800; bucketStart += bucketSize) {
      const bucketEnd = bucketStart + bucketSize;
      bucketDefinitions.push({
        label: `${formatCurrency(bucketStart)}-${formatCurrency(bucketEnd - 0.01)}`,
        min: bucketStart,
        max: bucketEnd,
        count: 0
      });
    }

    // Always include the $800+ bucket
    bucketDefinitions.push({
      label: `${formatCurrency(800)}+`,
      min: 800,
      max: Infinity,
      count: 0
    });

    numericBudgets.forEach(budget => {
      const bucket = bucketDefinitions.find(b => {
        if (b.max === Infinity) {
          return budget >= b.min;
        }
        return budget >= b.min && budget < b.max;
      });

      if (bucket) {
        bucket.count += 1;
      } else {
        // Fallback: if for some reason no bucket matches, count it in the $800+ bucket
        bucketDefinitions[bucketDefinitions.length - 1].count += 1;
      }
    });

    // Add "fuck it we ball" responses to the $800+ bucket
    bucketDefinitions[bucketDefinitions.length - 1].count += ballerCount;

    // Group days and their evening counterparts
    const dayAvailability = {};
    
    // Count each day and evening slot separately
    const days = [
      { date: '2026-01-15', label: 'Thu, Jan 15' },
      { date: '2026-01-15E', label: 'Thu, Jan 15 (Evening)' },
      { date: '2026-01-16', label: 'Fri, Jan 16' },
      { date: '2026-01-16E', label: 'Fri, Jan 16 (Evening)' },
      { date: '2026-01-17', label: 'Sat, Jan 17' },
      { date: '2026-01-18', label: 'Sun, Jan 18' },
      { date: '2026-01-19', label: 'Mon, Jan 19' }
    ];
    
    days.forEach(({ date, label }) => {
      const count = responses.filter(response => 
        (response.answers?.availability || []).includes(date)
      ).length;
      dayAvailability[label] = count;
    });

    return {
      availability: { available, maybe, unavailable, total: responses.length },
      sport: sportPreferences,
      budget: { 
        average: averageBudget,
        histogram: bucketDefinitions
      },
      dayAvailability
    };
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    
    try {
      let date;
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        // Firestore timestamp
        date = timestamp.toDate();
      } else if (timestamp instanceof Date) {
        // Already a Date object
        date = timestamp;
      } else if (typeof timestamp === 'string') {
        // ISO string
        date = new Date(timestamp);
      } else {
        // Fallback
        date = new Date(timestamp);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch (error) {
      console.error('Error formatting date:', error, timestamp);
      return 'Invalid Date';
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'N/A';
    return phone.replace(/(\+\d{1})(\d{3})(\d{3})(\d{4})/, '$1 ($2) $3-$4');
  };

  const escapeCSV = (value) => {
    if (value === null || value === undefined) return '';
    const stringValue = String(value);
    // If value contains comma, quote, or newline, wrap in quotes and escape quotes
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const formatDateForCSV = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      let date;
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else {
        date = new Date(timestamp);
      }
      
      if (isNaN(date.getTime())) {
        return '';
      }
      
      return date.toISOString();
    } catch (error) {
      return '';
    }
  };

  const handleExportCSV = () => {
    if (responses.length === 0) {
      alert('No responses to export.');
      return;
    }

    // Define CSV headers
    const headers = [
      'ID',
      'User ID',
      'Display Name',
      'Original Name',
      'Phone Number',
      'Submitted At',
      'MLK Weekend Availability',
      'Availability Days',
      'Sport',
      'Sport Days',
      'Budget',
      'Budget Max (Fuck it we ball)',
      'Transportation',
      'Transportation Seats',
      'Accommodation Preferences',
      'Dietary Restrictions',
      'Equipment Needs',
      'Equipment Extras',
      'Additional Notes'
    ];

    // Convert responses to CSV rows
    const rows = responses.map(response => {
      const answers = response.answers || {};
      const profile = userProfiles[response.userId];
      const displayName = profile?.displayName || response.userName || '';
      const originalName = profile?.originalName || response.userName || '';
      
      // Format availability array
      const availabilityDays = Array.isArray(answers.availability) 
        ? answers.availability.join('; ') 
        : '';
      
      // Format sport days array
      const sportDays = Array.isArray(answers.sportDays)
        ? answers.sportDays.join('; ')
        : '';
      
      // Format accommodation array
      const accommodation = Array.isArray(answers.accommodation)
        ? answers.accommodation.join('; ')
        : '';
      
      // Handle extras object
      const extras = answers.extras || {};
      const dietaryRestrictions = extras.dietaryRestrictions || '';
      const equipmentNeeds = extras.equipmentNeeds || '';
      const equipmentExtras = extras.equipmentExtras || '';
      
      // Format budget max checkbox
      const budgetMax = answers.budget_maxCheckbox ? 'Yes' : 'No';
      
      return [
        escapeCSV(response.id || ''),
        escapeCSV(response.userId || ''),
        escapeCSV(displayName),
        escapeCSV(originalName),
        escapeCSV(response.phoneNumber || ''),
        escapeCSV(formatDateForCSV(response.submittedAt)),
        escapeCSV(answers.mlkWeekendAvailability || ''),
        escapeCSV(availabilityDays),
        escapeCSV(answers.sport || ''),
        escapeCSV(sportDays),
        escapeCSV(answers.budget || ''),
        escapeCSV(budgetMax),
        escapeCSV(answers.transportation || ''),
        escapeCSV(answers.transportation_seats || ''),
        escapeCSV(accommodation),
        escapeCSV(dietaryRestrictions),
        escapeCSV(equipmentNeeds),
        escapeCSV(equipmentExtras),
        escapeCSV(answers.additionalNotes || '')
      ];
    });

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `whistler-poll-responses-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the URL object
    URL.revokeObjectURL(url);
  };

  const handleDeleteResponse = async (response) => {
    if (!window.confirm(`Are you sure you want to delete the response from ${response.userName}? This action cannot be undone.`)) {
      return;
    }

    try {
      // Try to delete from Firestore first
      if (response.id && !response.id.startsWith('local-')) {
        try {
          await deleteDoc(doc(db, 'responses', response.id));
          console.log('✅ Deleted from Firestore:', response.id);
        } catch (firestoreError) {
          console.warn('Firestore delete failed, trying localStorage:', firestoreError);
          // Fall through to localStorage deletion
        }
      }

      // Also delete from localStorage if it exists there
      try {
        const localResponses = JSON.parse(localStorage.getItem('localResponses') || '[]');
        const filteredLocal = localResponses.filter(r => {
          // Match by userId or phoneNumber
          return !(r.userId === response.userId && r.phoneNumber === response.phoneNumber);
        });
        localStorage.setItem('localResponses', JSON.stringify(filteredLocal));

        const adminData = JSON.parse(localStorage.getItem('adminData') || '[]');
        const filteredAdmin = adminData.filter(r => r.id !== response.id);
        localStorage.setItem('adminData', JSON.stringify(filteredAdmin));
        console.log('✅ Deleted from localStorage');
      } catch (localError) {
        console.warn('localStorage delete error:', localError);
      }
    } catch (error) {
      console.error('Error deleting response:', error);
      alert('Failed to delete response. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="card">
          <div className="loading">Loading responses...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="card">
          <div className="error">{error}</div>
        </div>
      </div>
    );
  }

  // Show loading while checking authentication
  if (!currentUser) {
    return (
      <div className="container">
        <div className="card">
          <div className="card-header">
            <h1 className="card-title">🔐 Admin Access Required</h1>
            <p className="card-subtitle">Please log in with an authorized phone number to access the admin dashboard</p>
          </div>
          
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <button 
              onClick={() => navigate('/login')} 
              className="btn btn-primary"
              style={{ width: 'auto', padding: '12px 24px' }}
            >
              Login with Phone Number
            </button>
          </div>
          
          <div style={{ textAlign: 'center', marginTop: '15px' }}>
            <button 
              onClick={() => navigate('/')} 
              className="btn btn-secondary"
              style={{ width: 'auto', padding: '10px 20px' }}
            >
              Back to Survey
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if user is authorized admin
  if (!isAdmin) {
    return (
      <div className="container">
        <div className="card">
          <div className="card-header">
            <h1 className="card-title">🚫 Access Denied</h1>
            <p className="card-subtitle">This phone number is not authorized to access the admin dashboard</p>
            <p style={{ color: 'var(--text-tertiary)', fontSize: '0.9rem', marginTop: '10px' }}>
              Logged in as: {currentUser.phoneNumber}
            </p>
          </div>
          
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <button 
              onClick={handleAdminLogout}
              className="btn btn-primary"
              style={{ width: 'auto', padding: '12px 24px' }}
            >
              Switch Account
            </button>
          </div>
          
          <div style={{ textAlign: 'center', marginTop: '15px' }}>
            <button 
              onClick={() => navigate('/')} 
              className="btn btn-secondary"
              style={{ width: 'auto', padding: '10px 20px' }}
            >
              Back to Survey
            </button>
          </div>
        </div>
      </div>
    );
  }

  const aggregatedData = getAggregatedData();
  const filteredResponses = getFilteredResponses();
  const maxBudgetBucketCount = aggregatedData.budget.histogram.reduce(
    (max, bucket) => Math.max(max, bucket.count),
    0
  );
  const safeBudgetBucketDenominator = maxBudgetBucketCount === 0 ? 1 : maxBudgetBucketCount;

  return (
    <div className="container">
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '15px' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <h1 className="card-title">📊 Admin Dashboard</h1>
              <p className="card-subtitle">Whistler Trip Planning - Response Overview</p>
              <p style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem', marginTop: '5px' }}>
                Logged in as: {currentUser.phoneNumber}
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '2px' }}>
                App version: {appVersion}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <button 
                onClick={handleExportCSV}
                className="btn btn-primary"
                style={{ 
                  width: 'auto', 
                  padding: '8px 16px', 
                  fontSize: '0.9rem',
                  borderRadius: '8px',
                  background: '#0ea5e9',
                  color: 'white',
                  border: '1px solid #0284c7',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                title="Export all responses to CSV"
              >
                📥 Export CSV
              </button>
              <button 
                onClick={() => navigate('/carpools', { state: { from: '/admin' } })}
                className="btn btn-primary"
                style={{ 
                  width: 'auto', 
                  padding: '8px 16px', 
                  fontSize: '0.9rem',
                  borderRadius: '8px',
                  background: '#10b981',
                  color: 'white',
                  border: '1px solid #059669'
                }}
              >
                🚗 Manage Carpools
              </button>
              <button 
                onClick={() => navigate('/trip-details/lodging-beds', { state: { from: '/admin' } })}
                className="btn btn-primary"
                style={{ 
                  width: 'auto', 
                  padding: '8px 16px', 
                  fontSize: '0.9rem',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none'
                }}
              >
                🛏️ Manage Lodging & Beds
              </button>
              <button 
                onClick={() => navigate('/home')}
                className="btn btn-secondary"
                style={{ 
                  width: 'auto', 
                  padding: '8px 16px', 
                  fontSize: '0.9rem',
                  borderRadius: '8px'
                }}
              >
                🏠 Home
              </button>
              <button 
                onClick={() => navigate('/survey')}
                className="btn btn-secondary"
                style={{ 
                  width: 'auto', 
                  padding: '8px 16px', 
                  fontSize: '0.9rem',
                  borderRadius: '8px'
                }}
              >
                View Survey
              </button>
              <button 
                onClick={handleAdminLogout}
                className="btn btn-secondary"
                style={{ 
                  width: 'auto', 
                  padding: '8px 16px', 
                  fontSize: '0.9rem',
                  borderRadius: '8px',
                  background: '#fed7d7',
                  color: '#c53030',
                  border: '1px solid #feb2b2'
                }}
              >
                Logout
              </button>
            </div>
          </div>
          {responses.length > 0 && responses[0]?.devMode && (
            <div style={{ 
              background: '#fef5e7', 
              padding: '12px', 
              borderRadius: '8px', 
              marginTop: '10px',
              border: '1px solid #f6e05e'
            }}>
              <p style={{ margin: 0, color: '#d69e2e', fontSize: '0.9rem', fontWeight: '600' }}>
                📱 Development Mode: Data stored locally (not in Firestore)
              </p>
            </div>
          )}
        </div>

        {error && (
          <div style={{ 
            background: '#fed7d7', 
            padding: '12px', 
            borderRadius: '8px', 
            marginBottom: '20px',
            border: '1px solid #feb2b2'
          }}>
            <p style={{ margin: 0, color: '#c53030', fontSize: '0.9rem' }}>
              {error}
            </p>
          </div>
        )}

        {/* Summary Cards */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '20px', 
          marginBottom: '30px' 
        }}>
          <div style={{ 
            background: '#c6f6d5', 
            padding: '20px', 
            borderRadius: '12px', 
            textAlign: 'center',
            border: '1px solid #9ae6b4'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#2f855a' }}>
              {aggregatedData.availability.available}
            </div>
            <div style={{ color: '#2f855a', fontWeight: '600' }}>Available</div>
          </div>
          
          <div style={{ 
            background: '#fef5e7', 
            padding: '20px', 
            borderRadius: '12px', 
            textAlign: 'center',
            border: '1px solid #f6e05e'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#d69e2e' }}>
              {aggregatedData.availability.maybe}
            </div>
            <div style={{ color: '#d69e2e', fontWeight: '600' }}>Maybe</div>
          </div>
          
          <div style={{ 
            background: '#fed7d7', 
            padding: '20px', 
            borderRadius: '12px', 
            textAlign: 'center',
            border: '1px solid #feb2b2'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#c53030' }}>
              {aggregatedData.availability.unavailable}
            </div>
            <div style={{ color: '#c53030', fontWeight: '600' }}>Unavailable</div>
          </div>
          
          <div style={{ 
            background: '#e6fffa', 
            padding: '20px', 
            borderRadius: '12px', 
            textAlign: 'center',
            border: '1px solid #81e6d9'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#319795' }}>
              {aggregatedData.availability.total}
            </div>
            <div style={{ color: '#319795', fontWeight: '600' }}>Total Responses</div>
          </div>
        </div>

        {/* Day Availability Chart */}
        <div style={{ 
                  background: 'var(--bg-secondary)', 
                  padding: '20px', 
                  borderRadius: '12px', 
                  marginBottom: '30px',
                  border: '1px solid var(--border-color)'
        }}>
          <h3 style={{ marginBottom: '15px', color: 'var(--text-primary)' }}>Day-by-Day Availability</h3>
          {Object.entries(aggregatedData.dayAvailability).map(([label, count]) => (
            <div key={label} style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '10px 0',
              borderBottom: '1px solid var(--border-color)'
            }}>
              <span style={{ fontWeight: '500' }}>
                {label}
              </span>
              <span style={{ 
                background: count > 0 ? '#c6f6d5' : '#fed7d7',
                color: count > 0 ? '#2f855a' : '#c53030',
                padding: '4px 12px',
                borderRadius: '20px',
                fontWeight: '600',
                fontSize: '0.9rem'
              }}>
                {count} people
              </span>
            </div>
          ))}
        </div>

        {/* Budget Analysis */}
        <div style={{ 
                  background: 'var(--bg-secondary)', 
                  padding: '20px', 
                  borderRadius: '12px', 
                  marginBottom: '30px',
                  border: '1px solid var(--border-color)'
        }}>
          <h3 style={{ marginBottom: '15px', color: 'var(--text-primary)' }}>Budget Analysis</h3>
          
          {/* Average Budget */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '20px',
            padding: '15px',
            background: 'var(--bg-primary)',
            borderRadius: '8px',
            border: '1px solid var(--border-color)'
          }}>
            <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>Average Budget:</span>
            <span style={{ 
              fontSize: '1.5rem', 
              fontWeight: 'bold', 
              color: '#0ea5e9'
            }}>
              ${aggregatedData.budget.average.toFixed(0)}
            </span>
          </div>

          {/* Budget Histogram */}
          <div style={{ marginBottom: '15px' }}>
            <h4 style={{ marginBottom: '10px', color: 'var(--text-secondary)', fontSize: '1rem' }}>Budget Distribution</h4>
            {aggregatedData.budget.histogram.map((bucket, index) => (
              <div key={index} style={{ marginBottom: '8px' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  marginBottom: '4px',
                  fontSize: '0.9rem',
                  color: 'var(--text-secondary)'
                }}>
                  <span>{bucket.label}</span>
                  <span>{bucket.count} people</span>
                </div>
                <div style={{ 
                  width: '100%', 
                  height: '20px', 
                  background: 'var(--border-color)', 
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{ 
                    width: `${(bucket.count / safeBudgetBucketDenominator) * 100}%`,
                    height: '100%',
                    background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sport Preferences */}
        <div style={{ 
                  background: 'var(--bg-secondary)', 
                  padding: '20px', 
                  borderRadius: '12px', 
                  marginBottom: '30px',
                  border: '1px solid var(--border-color)'
        }}>
          <h3 style={{ marginBottom: '15px', color: 'var(--text-primary)' }}>Sport Preferences</h3>
          
          {/* Pie Chart */}
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '15px',
            marginBottom: '20px'
          }}>
            {Object.entries(aggregatedData.sport).map(([sport, count]) => {
              const percentage = (count / aggregatedData.availability.total) * 100;
              const colors = {
                'skiing': '#0ea5e9',
                'snowboarding': '#0284c7', 
                'both': '#0369a1',
                'neither': '#6b7280'
              };
              
              return (
                <div key={sport} style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  padding: '10px 15px',
                  background: 'var(--bg-primary)',
                  borderRadius: '8px',
                  border: '1px solid var(--border-color)',
                  minWidth: '120px'
                }}>
                  <div style={{ 
                    width: '12px', 
                    height: '12px', 
                    borderRadius: '50%', 
                    background: colors[sport] || '#6b7280',
                    marginRight: '10px'
                  }} />
                  <div>
                    <div style={{ fontWeight: '600', color: 'var(--text-primary)', textTransform: 'capitalize' }}>
                      {sport}
                    </div>
                    <div style={{ fontSize: '0.9rem', color: 'var(--text-tertiary)' }}>
                      {count} ({percentage.toFixed(1)}%)
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ 
            display: 'flex', 
            gap: '10px', 
            flexWrap: 'wrap',
            marginBottom: '20px',
            borderBottom: '2px solid var(--border-color)',
            paddingBottom: '10px'
          }}>
            {[
              { id: 'responses', label: 'All Responses' },
              { id: 'waitlist', label: 'Waitlist' },
              { id: 'confirmed', label: 'Confirmed' },
              { id: 'dropped', label: 'Dropped Out' }
            ].map((tab) => {
              const count = tab.id === 'responses' ? responses.length :
                           tab.id === 'waitlist' ? responses.filter(r => r.status === 'waitlist' || r.isWaitlist === true).length :
                           tab.id === 'confirmed' ? responses.filter(r => r.status === 'confirmed').length :
                           responses.filter(r => r.status === 'dropped').length;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setFilter(tab.id === 'responses' ? 'all' : tab.id);
                  }}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px 8px 0 0',
                    border: 'none',
                    borderBottom: activeTab === tab.id ? '3px solid #667eea' : '3px solid transparent',
                    background: activeTab === tab.id ? 'var(--bg-primary)' : 'transparent',
                    color: activeTab === tab.id ? '#667eea' : 'var(--text-secondary)',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '0.95rem'
                  }}
                >
                  {tab.label} ({count})
                </button>
              );
            })}
          </div>
        </div>

        {/* Filter Buttons (for responses tab) */}
        {activeTab === 'responses' && (
          <div style={{ marginBottom: '20px' }}>
            <div style={{ 
              display: 'flex', 
              gap: '10px', 
              flexWrap: 'wrap',
              marginBottom: '20px'
            }}>
              {['all', 'yes', 'maybe', 'no'].map((filterType) => (
                <button
                  key={filterType}
                  onClick={() => setFilter(filterType)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '20px',
                    border: '2px solid var(--border-color)',
                    background: filter === filterType ? '#667eea' : 'var(--bg-primary)',
                    color: filter === filterType ? 'white' : 'var(--text-secondary)',
                    fontWeight: '600',
                    cursor: 'pointer',
                    textTransform: 'capitalize'
                  }}
                >
                  {filterType === 'yes' ? 'Available' : filterType === 'no' ? 'Unavailable' : filterType} 
                  ({filterType === 'all' ? responses.length : 
                    responses.filter(r => getAvailabilityStatus(r) === filterType).length})
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Responses List */}
        <div>
          <h3 style={{ marginBottom: '20px', color: 'var(--text-primary)' }}>
            Responses ({filteredResponses.length})
          </h3>
          
          {filteredResponses.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              color: 'var(--text-tertiary)',
              background: 'var(--bg-secondary)',
              borderRadius: '12px',
              border: '1px solid var(--border-color)'
            }}>
              No responses found for this filter.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {filteredResponses.map((response) => {
                const status = getAvailabilityStatus(response);
                const statusColors = {
                  yes: { bg: '#c6f6d5', color: '#2f855a' },
                  maybe: { bg: '#fef5e7', color: '#d69e2e' },
                  no: { bg: '#fed7d7', color: '#c53030' },
                  unavailable: { bg: '#fed7d7', color: '#c53030' } // fallback
                };

                return (
                  <div key={response.id} style={{
                    background: 'var(--bg-primary)',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px solid var(--border-color)',
                    boxShadow: '0 2px 4px var(--shadow)'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start',
                      marginBottom: '15px'
                    }}>
                      <div>
                        <h4 style={{ 
                          margin: '0 0 5px 0', 
                          color: 'var(--text-primary)',
                          fontSize: '1.1rem'
                        }}>
                          {(() => {
                            const profile = userProfiles[response.userId];
                            const displayName = profile?.displayName || response.userName;
                            const originalName = profile?.originalName || response.userName;
                            
                            if (profile && originalName !== displayName) {
                              return (
                                <span>
                                  {displayName}
                                  <span style={{
                                    marginLeft: '8px',
                                    fontSize: '0.85rem',
                                    color: 'var(--text-tertiary)',
                                    fontWeight: 'normal',
                                    fontStyle: 'italic'
                                  }}>
                                    (was: {originalName})
                                  </span>
                                </span>
                              );
                            }
                            return displayName;
                          })()}
                        </h4>
                        <p style={{ 
                          margin: '0 0 10px 0', 
                          color: 'var(--text-tertiary)',
                          fontSize: '0.9rem'
                        }}>
                          {formatPhoneNumber(response.phoneNumber)}
                        </p>
                        <p style={{ 
                          margin: 0, 
                          color: 'var(--text-muted)',
                          fontSize: '0.8rem'
                        }}>
                          Submitted: {formatDate(response.submittedAt)}
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{
                          background: response.status === 'waitlist' ? '#fff3cd' : 
                                     response.status === 'confirmed' ? '#c6f6d5' :
                                     response.status === 'dropped' ? '#fed7d7' :
                                     statusColors[status].bg,
                          color: response.status === 'waitlist' ? '#856404' :
                                 response.status === 'confirmed' ? '#2f855a' :
                                 response.status === 'dropped' ? '#c53030' :
                                 statusColors[status].color,
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontWeight: '600',
                          fontSize: '0.8rem',
                          textTransform: 'capitalize'
                        }}>
                          {response.status || status}
                        </span>
                        {response.status === 'waitlist' && (
                          <button
                            onClick={() => handleMoveToConfirmed(response)}
                            style={{
                              background: '#c6f6d5',
                              color: '#2f855a',
                              border: '1px solid #9ae6b4',
                              padding: '6px 12px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              fontWeight: '600'
                            }}
                          >
                            ✅ Confirm
                          </button>
                        )}
                        {response.status === 'confirmed' && (
                          <>
                            <button
                              onClick={() => handleMoveToWaitlist(response)}
                              style={{
                                background: '#fff3cd',
                                color: '#856404',
                                border: '1px solid #ffc107',
                                padding: '6px 12px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: '600'
                              }}
                            >
                              ⏳ Move to Waitlist
                            </button>
                            <button
                              onClick={() => handleMarkAsDropped(response)}
                              style={{
                                background: '#fed7d7',
                                color: '#c53030',
                                border: '1px solid #feb2b2',
                                padding: '6px 12px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: '600'
                              }}
                            >
                              ❌ Mark Dropped
                            </button>
                          </>
                        )}
                        {response.status === 'dropped' && (
                          <button
                            onClick={() => handleMoveToWaitlist(response)}
                            style={{
                              background: '#fff3cd',
                              color: '#856404',
                              border: '1px solid #ffc107',
                              padding: '6px 12px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              fontWeight: '600'
                            }}
                          >
                            ⏳ Move to Waitlist
                          </button>
                        )}
                        {!response.status && (
                          <>
                            <button
                              onClick={() => handleMoveToConfirmed(response)}
                              style={{
                                background: '#c6f6d5',
                                color: '#2f855a',
                                border: '1px solid #9ae6b4',
                                padding: '6px 12px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: '600'
                              }}
                            >
                              ✅ Confirm
                            </button>
                            <button
                              onClick={() => handleMoveToWaitlist(response)}
                              style={{
                                background: '#fff3cd',
                                color: '#856404',
                                border: '1px solid #ffc107',
                                padding: '6px 12px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: '600'
                              }}
                            >
                              ⏳ Waitlist
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDeleteResponse(response)}
                          style={{
                            background: '#fed7d7',
                            color: '#c53030',
                            border: '1px solid #feb2b2',
                            padding: '6px 12px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseOver={(e) => {
                            e.target.style.background = '#fc8181';
                            e.target.style.borderColor = '#f56565';
                          }}
                          onMouseOut={(e) => {
                            e.target.style.background = '#fed7d7';
                            e.target.style.borderColor = '#feb2b2';
                          }}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>

                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                      gap: '15px',
                      fontSize: '0.9rem'
                    }}>
                      <div>
                        <strong style={{ color: 'var(--text-secondary)' }}>Availability:</strong>
                        <div style={{ color: 'var(--text-tertiary)', marginTop: '2px' }}>
                          {(response.answers?.availability || []).map(day => {
                            // Handle evening dates
                            if (day.endsWith('E')) {
                              const baseDate = day.replace('E', '');
                              const date = new Date(baseDate);
                              return date.toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric' 
                              }) + ' (Eve)';
                            }
                            return new Date(day).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric' 
                            });
                          }).join(', ') || 'None selected'}
                        </div>
                      </div>
                      
                      <div>
                        <strong style={{ color: 'var(--text-secondary)' }}>Sport:</strong>
                        <div style={{ color: 'var(--text-tertiary)', marginTop: '2px' }}>
                          {response.answers?.sport || 'Not specified'}
                        </div>
                      </div>
                      
                      <div>
                        <strong style={{ color: 'var(--text-secondary)' }}>Budget:</strong>
                        <div style={{ color: 'var(--text-tertiary)', marginTop: '2px' }}>
                          ${response.answers?.budget || 'Not specified'}
                          {response.answers?.budget_maxCheckbox && (
                            <span style={{ 
                              marginLeft: '8px',
                              background: 'linear-gradient(135deg, #fef5e7 0%, #f6e05e 100%)',
                              color: '#d69e2e',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '0.8rem',
                              fontWeight: '600'
                            }}>
                              💸 Fuck it we ball
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div>
                        <strong style={{ color: 'var(--text-secondary)' }}>Transportation:</strong>
                        <div style={{ color: 'var(--text-tertiary)', marginTop: '2px' }}>
                          {response.answers?.transportation ? 
                            (response.answers.transportation === 'drive' && response.answers.transportation_seats !== undefined ? 
                              `Drive (${response.answers.transportation_seats} seats)` : 
                              response.answers.transportation) : 
                            'Not specified'}
                        </div>
                      </div>
                    </div>

                    {response.answers?.additionalNotes && (
                      <div style={{ marginTop: '15px' }}>
                        <strong style={{ color: 'var(--text-secondary)' }}>Notes:</strong>
                        <div style={{ 
                          color: 'var(--text-tertiary)', 
                          marginTop: '2px',
                          fontStyle: 'italic'
                        }}>
                          {response.answers.additionalNotes}
                        </div>
                      </div>
                    )}

                    {/* Carpool Assignment Section */}
                    {(response.status === 'confirmed' || !response.status) && (
                      <div style={{ 
                        marginTop: '15px', 
                        padding: '15px', 
                        background: 'var(--bg-secondary)',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                          <h5 style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
                            🚗 Carpool Assignment
                          </h5>
                        </div>
                        {(() => {
                          const userCarpool = getUserCarpool(response.userId);
                          if (userCarpool) {
                            return (
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                                <span style={{ color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                                  Currently in: <strong>{userCarpool.title}</strong> ({userCarpool.driver})
                                </span>
                                <button
                                  onClick={() => handleRemoveFromCarpool(response.userId)}
                                  style={{
                                    background: '#fed7d7',
                                    color: '#c53030',
                                    border: '1px solid #feb2b2',
                                    padding: '4px 8px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '0.8rem',
                                    fontWeight: '600'
                                  }}
                                >
                                  Remove
                                </button>
                              </div>
                            );
                          } else {
                            return (
                              <div>
                                {assigningToCarpool === response.userId ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <select
                                      onChange={(e) => {
                                        if (e.target.value) {
                                          handleAssignToCarpool(response.userId, e.target.value);
                                        }
                                      }}
                                      style={{
                                        padding: '6px 10px',
                                        borderRadius: '6px',
                                        border: '2px solid var(--border-color)',
                                        fontSize: '0.85rem',
                                        background: 'var(--bg-primary)',
                                        color: 'var(--text-primary)'
                                      }}
                                    >
                                      <option value="">Select a carpool...</option>
                                      {carpools
                                        .filter(c => (c.passengers?.length || 0) < c.maxSpots)
                                        .map(carpool => (
                                          <option key={carpool.id} value={carpool.id}>
                                            {carpool.title} - {carpool.driver} ({carpool.maxSpots - (carpool.passengers?.length || 0)} spots available)
                                          </option>
                                        ))}
                                    </select>
                                    <button
                                      onClick={() => setAssigningToCarpool(null)}
                                      style={{
                                        background: 'var(--bg-primary)',
                                        color: 'var(--text-secondary)',
                                        border: '1px solid var(--border-color)',
                                        padding: '4px 8px',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '0.8rem'
                                      }}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                ) : (
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                                    <span style={{ color: 'var(--text-tertiary)', fontSize: '0.9rem' }}>
                                      Not assigned to a carpool
                                    </span>
                                    <button
                                      onClick={() => setAssigningToCarpool(response.userId)}
                                      style={{
                                        background: '#c6f6d5',
                                        color: '#2f855a',
                                        border: '1px solid #9ae6b4',
                                        padding: '4px 8px',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '0.8rem',
                                        fontWeight: '600'
                                      }}
                                    >
                                      Assign to Carpool
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          }
                        })()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
